'use client';

import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { H4 } from '@/components/ui/typography';
import { cn } from '@/lib/shared/ui';
import { Pause, Play, SlidersVertical, X } from 'lucide-react';
import React from 'react';

export type LevelType = {
  id: string;
  label: string;
  start: {
    x: number;
    y: number;
  };
  levels: {
    label: string;
    value: number;
  }[];
};

export const LevelsResults = ({
  levels,
  isPlaying,
  onPlayPause,
}: {
  levels: {
    label: string;
    value: number;
  }[];
  isPlaying: boolean;
  onPlayPause: () => void;
}) => {
  return (
    <div className={cn('flex h-full flex-col gap-3 p-3 bg-accent/30 rounded-lg')}>
      <H4 className="text-base text-center">Object</H4>

      <div className="flex-1 flex gap-5 justify-center">
        {levels.map((level, i) => (
          <div className="flex flex-col gap-1 w-6 items-center" key={i}>
            <Slider
              defaultValue={[level.value]}
              max={100}
              step={1}
              orientation="vertical"
              value={[level.value]}
              disabled
              color="accent"
            />
            <div>{level.value}</div>
            <div>{level.label}</div>
          </div>
        ))}
      </div>
      <Button
        onClick={onPlayPause}
        className="bg-accent hover:bg-accent text-background-color min-w-28"
      >
        {isPlaying ? <Pause /> : <Play />}
        {isPlaying ? 'Pause' : 'Play'}
      </Button>
    </div>
  );
};

export const sizes = {
  object: 45,
  level: 45,
};

const levelsClasses = `top-0 left-0 w-10 h-10 bg-accent-three rounded text-background-color flex items-center justify-center relative border-4 border-transparent`;

export const LevelMapItem = ({ isActive, level }: { isActive: boolean; level: LevelType }) => {
  return (
    <div
      className={cn(levelsClasses, isActive && 'border-4 border-violet-200', 'flex-col')}
      style={{
        width: `${sizes.level}px`,
        height: `${sizes.level}px`,
      }}
    >
      <div className="text-center text-xs absolute -top-5 text-white font-bold w-[5rem]">
        {level.label}
      </div>
      <SlidersVertical />
    </div>
  );
};

export const LevelsControls = ({
  id,
  isActive = false,
  levels,
  onRemove,
  onValueChange,
  onClick,
}: {
  id: string;
  isActive?: boolean;
  levels: { label: string; value: number }[];
  onValueChange: (index: number, value: number) => void;
  onClick?: () => void;
  onRemove?: () => void;
}) => {
  return (
    <div
      onClick={onClick}
      className={cn(
        'flex flex-col gap-3 p-3 bg-accent-three/30 rounded-lg opacity-50 border-2 border-transparent relative',
        isActive && 'border-2 opacity-100',
      )}
    >
      <div className="absolute -top-3 -right-3">
        <Button
          onClick={onRemove}
          className="h-6 w-6 text-xs rounded-full p-0 bg-[#312295] hover:bg-[#312295] border-[#011367] border-2"
          variant="outline"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
      <H4 className="text-base text-center">Levels {id}</H4>
      <div className="flex-1 flex gap-5">
        {levels.map((level, i) => (
          <div className="flex flex-col gap-1 w-6 items-center" key={i}>
            <Slider
              defaultValue={[level.value]}
              max={100}
              step={1}
              orientation="vertical"
              value={[level.value]}
              onValueChange={(value) => {
                onValueChange(i, value[0]);
              }}
            />
            <div>{level.value}</div>
            <div>{level.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
